import { useEffect, useMemo, useState } from 'react'
import { colours, sizes } from '@qflow/theme'

import {
  ApplyButtonContainer,
  StyledUpdateSection,
  NoTagsMessage,
  TagInputContainer,
  UntaggingTagButton,
  UntaggingTagList
} from 'portal/components/CommonStyles/ActionStripStyledComponents'
import { ButtonWithMessageContainer } from 'portal/components/CommonStyles/AssignmentStripStyledComponents'

import { ColourButton, Text } from 'portal/lib/primitives'

type UntaggingStripProps = {
  relatedTags?: string[]
  onApply: (tags: string[]) => void
  recordCount: number
}

export function UntaggingStrip({
  relatedTags,
  onApply,
  recordCount
}: UntaggingStripProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const tags = useMemo(
    () =>
      [...new Set((relatedTags || []).filter(tag => tag && tag.trim()))].sort(
        (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [relatedTags]
  )

  useEffect(() => {
    setSelectedTags(current => current.filter(tag => tags.includes(tag)))
  }, [tags])

  const isApplyDisabled = selectedTags.length === 0

  const handleTagToggle = (tag: string) => {
    setSelectedTags(current => {
      if (current.includes(tag)) {
        return current.filter(t => t !== tag)
      }
      return [...current, tag]
    })
  }

  const handleApply = () => {
    onApply(selectedTags)
  }

  return (
    <StyledUpdateSection>
      <h3>Untag Records</h3>
      <TagInputContainer>
        <div style={{ margin: '0 0 0.5rem 0' }}>
          <Text size={sizes.SMALL_MEDIUM}>
            Below are the tags found on the selected records. Click to select
            which tags you would like to remove.
          </Text>
        </div>
        {tags.length === 0 ? (
          <NoTagsMessage>
            <Text bold>No tags found on selected records</Text>
          </NoTagsMessage>
        ) : (
          <UntaggingTagList>
            {tags.map(tag => {
              const selected = selectedTags.includes(tag)

              return (
                <UntaggingTagButton
                  key={tag}
                  type="button"
                  $selected={selected}
                  onClick={() => handleTagToggle(tag)}
                >
                  {tag}
                </UntaggingTagButton>
              )
            })}
          </UntaggingTagList>
        )}
        <div style={{ margin: '0 0.25rem 0 0.25rem', textAlign: 'right' }}>
          <Text size={sizes.X_SMALL}>
            Selected tags appear in{' '}
            <span style={{ color: colours.ORANGE_TEXT }}>orange</span>. Click
            again to deselect.
          </Text>
        </div>
      </TagInputContainer>
      <ApplyButtonContainer>
        <ButtonWithMessageContainer>
          <ColourButton.Caution
            type="button"
            onClick={handleApply}
            disabled={isApplyDisabled}
          >
            Apply to {recordCount} record{recordCount > 1 && 's'}
          </ColourButton.Caution>
        </ButtonWithMessageContainer>
      </ApplyButtonContainer>
    </StyledUpdateSection>
  )
}
